import React from 'react';
import {
    User, Mail, Phone, Save, Key, Loader2, Building2,
} from 'lucide-react';
import PageShell from './PageShell';
import { getAccountApi, updateProfileApi, changePasswordApi } from '../services/myAccountApi';

const MyAccountPage = () => {
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [success, setSuccess] = React.useState(null);

    // ── Personal info ──────────────────────────────────────
    // userType drives which name fields render:
    //   CLIENT (fleet customer portal login) → a single "Business name"
    //   OWNER / STAFF                        → First name + Last name
    // Fleet customers are BUSINESS customers, so firstName/lastName come back
    // null for them and businessName carries the trading name.
    const [formData, setFormData] = React.useState({
        userType: '',
        firstName: '',
        lastName: '',
        businessName: '',
        email: '',
        phone: '',
    });

    const isClient = formData.userType === 'CLIENT';

    const handleChange = (field) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

    // ── Password ───────────────────────────────────────────
    const [showChangePassword, setShowChangePassword] = React.useState(false);
    const [passwordData, setPasswordData] = React.useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
    });

    const handlePasswordChange = (field) => (e) => {
        setPasswordData((prev) => ({ ...prev, [field]: e.target.value }));
    };

    // ── Load account data on mount ─────────────────────────
    React.useEffect(() => {
        getAccountApi()
            .then((data) => {
                setFormData({
                    userType: data.userType || '',
                    firstName: data.firstName || '',
                    lastName: data.lastName || '',
                    businessName: data.businessName || '',
                    email: data.email || '',
                    phone: data.phone || '',
                });
            })
            .catch(() => setError('Failed to load account data'))
            .finally(() => setLoading(false));
    }, []);

    // ── Flash message helper ───────────────────────────────
    const flash = (msg) => {
        setSuccess(msg);
        setTimeout(() => setSuccess(null), 3000);
    };

    // ── Save handlers ──────────────────────────────────────
    const handleSaveProfile = async () => {
        setSaving(true);
        setError(null);
        try {
            // Send only the fields relevant to this user type. The backend
            // validates presence per-type (business name for CLIENT, first/last
            // name for STAFF/OWNER).
            const payload = isClient
                ? { businessName: formData.businessName, phone: formData.phone }
                : { firstName: formData.firstName, lastName: formData.lastName, phone: formData.phone };

            const data = await updateProfileApi(payload);

            setFormData((prev) => ({
                ...prev,
                userType: data.userType || prev.userType,
                firstName: data.firstName || '',
                lastName: data.lastName || '',
                businessName: data.businessName || '',
                phone: data.phone || '',
            }));
            flash('Profile saved');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save profile');
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async () => {
        setSaving(true);
        setError(null);
        try {
            await changePasswordApi(passwordData);
            setPasswordData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
            setShowChangePassword(false);
            flash('Password updated');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to change password');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => {
        handleSaveProfile();
    };

    // ── Shared input classes ───────────────────────────────
    const inputCls =
        'w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none';

    // ── Loading state ──────────────────────────────────────
    if (loading) {
        return (
            <PageShell title="My Account" description="Manage your profile and preferences" icon={User}>
                <div className="flex items-center justify-center py-20">
                    <Loader2 size={24} className="animate-spin text-gray-400" />
                </div>
            </PageShell>
        );
    }

    return (
        <PageShell
            title="My Account"
            description="Manage your profile and preferences"
            icon={User}
        >
            <div className="max-w-2xl">
                {/* ─── Feedback ──────────────────────────────────── */}
                {error && (
                    <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="mb-4 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
                        {success}
                    </div>
                )}

                {/* ─── Profile ───────────────────────────────────── */}
                <div className="space-y-6">
                    {/* Personal / Business Information */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <h3 className="font-semibold text-gray-900 mb-6">
                            {isClient ? 'Business information' : 'Personal information'}
                        </h3>

                        <div className="space-y-5">
                            {isClient ? (
                                /* Fleet customer — a single trading name. */
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Business name
                                    </label>
                                    <div className="relative">
                                        <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                        <input
                                            type="text"
                                            value={formData.businessName}
                                            onChange={handleChange('businessName')}
                                            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                                            placeholder="Acme Couriers Pty Ltd"
                                        />
                                    </div>
                                </div>
                            ) : (
                                /* Workshop staff / owner — first + last name. */
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            First name
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.firstName}
                                            onChange={handleChange('firstName')}
                                            className={inputCls}
                                            placeholder="John"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Last name
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.lastName}
                                            onChange={handleChange('lastName')}
                                            className={inputCls}
                                            placeholder="Smith"
                                        />
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Email address
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <input
                                        type="email"
                                        value={formData.email}
                                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-not-allowed"
                                        disabled
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    {isClient
                                        ? 'Contact your workshop to change your email address'
                                        : 'Contact support to change your email address'}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Phone number
                                </label>
                                <div className="relative">
                                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={handleChange('phone')}
                                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                                        placeholder="04XX XXX XXX"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Password */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                                <Key size={18} className="text-gray-600" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900">Password</h3>
                                <p className="text-sm text-gray-500">Change your account password</p>
                            </div>
                        </div>

                        {showChangePassword ? (
                            <div className="space-y-4 pt-4 border-t border-gray-100">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Current password
                                    </label>
                                    <input
                                        type="password"
                                        value={passwordData.currentPassword}
                                        onChange={handlePasswordChange('currentPassword')}
                                        className={inputCls}
                                        placeholder="••••••••"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        New password
                                    </label>
                                    <input
                                        type="password"
                                        value={passwordData.newPassword}
                                        onChange={handlePasswordChange('newPassword')}
                                        className={inputCls}
                                        placeholder="••••••••"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Confirm new password
                                    </label>
                                    <input
                                        type="password"
                                        value={passwordData.confirmNewPassword}
                                        onChange={handlePasswordChange('confirmNewPassword')}
                                        className={inputCls}
                                        placeholder="••••••••"
                                    />
                                </div>
                                <div className="flex gap-3 pt-2">
                                    <button
                                        onClick={() => {
                                            setShowChangePassword(false);
                                            setPasswordData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleChangePassword}
                                        disabled={saving}
                                        className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        {saving ? 'Updating…' : 'Update password'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowChangePassword(true)}
                                className="text-sm font-medium text-emerald-600 hover:text-emerald-700"
                            >
                                Change password
                            </button>
                        )}
                    </div>
                </div>

                {/* ─── Save Button ───────────────────────────────── */}
                <div className="flex justify-end pt-6 pb-6">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
                    >
                        {saving ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : (
                            <Save size={16} />
                        )}
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </div>
        </PageShell>
    );
};

export default MyAccountPage;